import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Printer, QrCode, Pencil, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { BRAND } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const EMPTY_PRICING = { monthly: "", basis: "", setup: "", included: [], extras: [], trial: "", note: "" };

const HOW = [
  { n: "1", t: "We sync your website", b: "Your inventory loads itself — pricing, photos, mileage, the day each unit hit the lot. New arrivals appear overnight; sold units archive themselves." },
  { n: "2", t: "You print a sticker per car", b: "One page, sorted by rooftop. Every sticker carries the stock number, VIN and a QR code that points at that exact car." },
  { n: "3", t: "The shopper scans it", b: "They get your branded page in about a second: gallery, specs, vehicle history, time on the lot — on any phone, any hour." },
  { n: "4", t: "You get the lead in your CRM", b: "Name and mobile in exchange for your best price, delivered as ADF to the right rooftop's inbox with the car and the price they were shown." },
];

const WALLS = [
  ["Lower price", "A set amount or percent off, revealed after contact"],
  ["CARFAX report", "The shopper asks, you send it — no fabricated report links"],
  ["Reconditioning", "Your recon status, notes and the shop's document"],
  ["Time on lot", "Days since the car hit your lot, straight from your site"],
  ["How you got it", "Local trade, lease return, auction, dealer purchase"],
  ["As-is estimate", "The repair estimate for units sold as-is"],
];

export default function OnePager() {
  const [params] = useSearchParams();
  const [preparedFor, setPreparedFor] = useState(params.get("for") || "");
  const [qr, setQr] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/public/demo/qr", { params: { target: "try" } }).then(({ data }) => setQr(data)).catch(() => {});
    api.get("/public/pricing").then(({ data }) => setPricing(data)).catch(() => setPricing(EMPTY_PRICING));
  }, []);

  const savePricing = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/platform/pricing", {
        ...pricing,
        included: (pricing.includedText ?? (pricing.included || []).join("\n")).split("\n").filter((s) => s.trim()),
        extras: (pricing.extrasText ?? (pricing.extras || []).join("\n")).split("\n").filter((s) => s.trim()),
      });
      setPricing(data);
      setEditing(false);
      toast.success("Pricing saved — it prints on every sheet now");
    } catch (e) {
      toast.error(e.response?.status === 401 || e.response?.status === 403
        ? "Sign in as Vinconex staff to save pricing"
        : formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const hasPrice = !!(pricing?.monthly || pricing?.setup || (pricing?.included || []).length);

  return (
    <div className="min-h-screen bg-[#05070C] py-8 print:bg-white print:py-0">
      <style>{"@page { size: letter portrait; margin: 12mm; }"}</style>

      <div className="no-print max-w-[860px] mx-auto px-5 mb-5 flex flex-col sm:flex-row sm:items-end gap-3">
        <div className="flex-1">
          <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-tech mb-1.5">Prepared for (prints on the sheet)</label>
          <Input value={preparedFor} onChange={(e) => setPreparedFor(e.target.value)} placeholder="Honesdale Chrysler Dodge Jeep Ram"
                 data-testid="onepager-for-input" className="bg-slate-950 border-slate-800 focus:border-[#0A8CF2] text-slate-100" />
        </div>
        <Button onClick={() => setEditing((e) => !e)} variant="outline" data-testid="onepager-edit-pricing-btn"
                className="h-10 border-slate-700 bg-slate-900/50 hover:bg-slate-800 text-slate-200">
          <Pencil className="mr-2 h-4 w-4" /> {editing ? "Close pricing" : "Edit pricing"}
        </Button>
        <Button onClick={() => window.print()} data-testid="onepager-print-btn" className="h-10 bg-[#0A8CF2] hover:bg-[#3AA3FF] font-bold">
          <Printer className="mr-2 h-4 w-4" /> Print / Save as PDF
        </Button>
      </div>

      {editing && pricing && (
        <div className="no-print max-w-[860px] mx-auto px-5 mb-5 rounded-xl border border-[#2A2E3D] bg-[#0B0D14] p-5" data-testid="onepager-pricing-editor">
          <p className="text-xs text-slate-400 mb-3">Whatever you type here prints in the <b className="text-slate-200">Investment</b> block — leave it all blank and the sheet says “pricing on request” instead. Staff sign-in required to save.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {[["monthly", "Monthly price, e.g. $399/mo"], ["basis", "Per rooftop or per group, e.g. per rooftop"],
              ["setup", "Setup / onboarding fee, e.g. $500 one-time"], ["trial", "Trial or pilot, e.g. First 30 days free"]].map(([k, ph]) => (
              <div key={k}>
                <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-tech mb-1.5">{k}</label>
                <Input value={pricing[k] || ""} onChange={(e) => setPricing({ ...pricing, [k]: e.target.value })} placeholder={ph}
                       data-testid={`pricing-${k}-input`} className="bg-slate-950 border-slate-800 focus:border-[#0A8CF2] text-slate-100" />
              </div>
            ))}
            {[["included", "What's included — one per line"], ["extras", "Add-ons / extras — one per line"]].map(([k, ph]) => (
              <div key={k}>
                <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-tech mb-1.5">{k}</label>
                <textarea rows={4} placeholder={ph} data-testid={`pricing-${k}-input`}
                          value={pricing[`${k}Text`] ?? (pricing[k] || []).join("\n")}
                          onChange={(e) => setPricing({ ...pricing, [`${k}Text`]: e.target.value })}
                          className="w-full rounded-md bg-slate-950 border border-slate-800 focus:border-[#0A8CF2] text-slate-100 text-sm p-2 outline-none" />
              </div>
            ))}
          </div>
          <div className="mt-3 flex items-center gap-3">
            <Button onClick={savePricing} disabled={saving} data-testid="pricing-save-btn" className="bg-[#0A8CF2] hover:bg-[#3AA3FF] font-bold">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save pricing"}
            </Button>
            <span className="text-xs text-slate-500">Nothing is invented — the sheet prints exactly what's here.</span>
          </div>
        </div>
      )}

      <div className="mx-auto w-full max-w-[860px] bg-white text-[#0B0D14] rounded-lg shadow-2xl p-10 print:rounded-none print:shadow-none print:p-0 print:max-w-none"
           data-testid="onepager-sheet">
        <header className="flex items-start justify-between gap-6 border-b-2 border-[#0A8CF2] pb-4">
          <div>
            <img src={BRAND.wordmark} alt={BRAND.name} className="h-7 w-auto" />
            <p className="mt-2 text-[13px] font-semibold text-[#0A8CF2] tracking-wide">Every car answers for itself — even when nobody's on the lot.</p>
            {preparedFor && <p className="mt-1 text-[11px] text-[#4B5563]" data-testid="onepager-prepared-for">Prepared for {preparedFor}</p>}
          </div>
          <div className="text-right text-[10px] leading-relaxed text-[#4B5563] shrink-0">
            <div className="font-bold text-[#0B0D14] text-[11px]">www.vinconex.com</div>
            <div>{BRAND.phone} · contact@vinconex.com</div>
            <div>Operated by {BRAND.owner}</div>
          </div>
        </header>

        <section className="mt-5 grid grid-cols-3 gap-5 items-start">
          <div className="col-span-2">
            <h1 className="text-[22px] font-black leading-tight tracking-tight">Half your shoppers walk the lot when your showroom is dark.</h1>
            <p className="mt-2 text-[12px] leading-relaxed text-[#374151]">
              They look at a window sticker, don't find a price, and drive to the next store — and you never learn they were there.
              Vinconex puts a QR code on every car you own. A shopper scans it, gets your branded page, and leaves a name and a mobile
              number to see your best price. The lead is in your CRM before they're off the lot, routed to the rooftop that owns the car.
            </p>
          </div>
          <div className="border border-[#E5E7EB] rounded-lg p-3 text-center">
            {qr?.qr ? <img src={qr.qr} alt="Scan to try the demo" className="w-full max-w-[168px] mx-auto" data-testid="onepager-qr" /> : <QrCode className="h-16 w-16 mx-auto text-[#9CA3AF]" />}
            <p className="mt-1 text-[9px] text-[#6B7280] break-all">{qr?.url || "www.vinconex.com/try"}</p>
          </div>
        </section>

        <section className="mt-4">
          <h2 className="text-[11px] font-black uppercase tracking-[0.2em] text-[#0A8CF2]">How it works</h2>
          <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-3">
            {HOW.map((s) => (
              <div key={s.n} className="flex gap-2.5">
                <span className="h-6 w-6 shrink-0 rounded bg-[#0A8CF2] text-white text-[11px] font-black flex items-center justify-center">{s.n}</span>
                <div>
                  <div className="text-[12px] font-bold leading-tight">{s.t}</div>
                  <p className="text-[11px] leading-snug text-[#4B5563] mt-0.5">{s.b}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-4 grid grid-cols-2 gap-6">
          <div>
            <h2 className="text-[11px] font-black uppercase tracking-[0.2em] text-[#0A8CF2]">What a shopper trades contact info for</h2>
            <ul className="mt-2 space-y-1">
              {WALLS.map(([t, b]) => (
                <li key={t} className="text-[11px] leading-snug">
                  <span className="font-bold">{t}.</span> <span className="text-[#4B5563]">{b}</span>
                </li>
              ))}
            </ul>
            <p className="mt-2 text-[10px] text-[#6B7280]">You choose which ones are on. Off by default.</p>
          </div>
          <div>
            <h2 className="text-[11px] font-black uppercase tracking-[0.2em] text-[#0A8CF2]">Built for a real store</h2>
            <ul className="mt-2 space-y-1 text-[11px] leading-snug text-[#374151]">
              <li><b>Multi-rooftop routing.</b> Each car belongs to a store; each store has its own CRM inbox.</li>
              <li><b>Works with your CRM.</b> ADF 1.0 lead email — DriveCentric, VinSolutions, eLead, Dealersocket.</li>
              <li><b>Your branding.</b> Your logo, colors and phone number on every page a shopper sees.</li>
              <li><b>Scan analytics.</b> See which cars people actually walk up to, and when.</li>
              <li><b>Dealer.com, RideMotive and more.</b> If your site lists inventory, we sync it.</li>
              <li><b>Security first.</b> MFA, audit logging, nightly backups, retention controls, no shopper data sold.</li>
            </ul>
          </div>
        </section>

        <section className="mt-3 border border-[#E5E7EB] rounded-lg p-3 bg-[#F8FAFC]" data-testid="onepager-pricing">
          <h2 className="text-[11px] font-black uppercase tracking-[0.2em] text-[#0A8CF2]">The investment</h2>
          {hasPrice ? (
            <>
              <div className="mt-2 flex flex-wrap items-end gap-x-8 gap-y-2">
                {pricing.monthly && (
                  <div>
                    <div className="text-[22px] font-black leading-none">{pricing.monthly}</div>
                    <div className="text-[10px] uppercase tracking-wider text-[#6B7280] mt-1">{pricing.basis || "per month"}</div>
                  </div>
                )}
                {pricing.setup && (
                  <div>
                    <div className="text-[15px] font-black leading-none">{pricing.setup}</div>
                    <div className="text-[10px] uppercase tracking-wider text-[#6B7280] mt-1">setup / onboarding</div>
                  </div>
                )}
                {pricing.trial && (
                  <div className="rounded bg-[#0A8CF2] text-white px-2.5 py-1 text-[11px] font-bold">{pricing.trial}</div>
                )}
              </div>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-2">
                {(pricing.included || []).length > 0 && (
                  <div className="sm:col-span-2">
                    <div className="text-[10px] font-black uppercase tracking-wider text-[#374151]">Included</div>
                    <ul className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5">
                      {pricing.included.map((i) => <li key={i} className="text-[11px] leading-snug text-[#4B5563]">· {i}</li>)}
                    </ul>
                  </div>
                )}
                {(pricing.extras || []).length > 0 && (
                  <div>
                    <div className="text-[10px] font-black uppercase tracking-wider text-[#374151]">Add-ons</div>
                    <ul className="mt-1 space-y-0.5">
                      {pricing.extras.map((i) => <li key={i} className="text-[11px] leading-snug text-[#4B5563]">· {i}</li>)}
                    </ul>
                  </div>
                )}
              </div>
              {pricing.note && <p className="mt-1.5 text-[10px] text-[#6B7280]">{pricing.note}</p>}
            </>
          ) : (
            <div className="mt-2 flex flex-wrap items-center justify-between gap-4">
              <div>
                <div className="text-[15px] font-black">Pricing on request — sized to your store</div>
                <p className="text-[11px] text-[#4B5563] mt-0.5">A 15-minute call covers rooftops, inventory size and how your CRM takes leads. No contracts to read first.</p>
              </div>
              <div className="text-[11px] font-bold text-[#0A8CF2] text-right">{BRAND.phone}<br />contact@vinconex.com<br />www.vinconex.com/contact</div>
            </div>
          )}
        </section>

        <footer className="mt-4 pt-3 border-t border-[#E5E7EB] flex items-end justify-between gap-4">
          <div>
            <div className="text-[12px] font-black">Want it on your inventory?</div>
            <p className="text-[11px] text-[#4B5563] mt-0.5">Send us your website address — we'll sync it and show you your own cars with QR codes on them,
              before you commit to anything. {BRAND.phone} · contact@vinconex.com · www.vinconex.com/contact</p>
          </div>
          <div className="text-[9px] text-[#9CA3AF] text-right shrink-0">
            {BRAND.name} · {BRAND.owner}<br />Scan. Connect. Drive Sales.
          </div>
        </footer>
      </div>

      <p className="no-print max-w-[860px] mx-auto px-5 mt-4 text-xs text-slate-600">
        Letter portrait, one page. The QR code points at the live click-through demo ({qr?.url || "/try"}) so a manager can try the shopper side from the printed sheet.
      </p>
    </div>
  );
}
